import React, { useRef } from "react";
import secBg from "../../assets/images/home/network/secBg.webp";
import secImg from "../../assets/images/home/network/secImg.webp";
import { PiArrowBendDoubleUpRightFill } from "react-icons/pi";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Network = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  // Container variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };
  
  // Item variants for individual elements
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  // Image animation variants
  const imageVariants = {
    hidden: { 
      scale: 0.8, 
      opacity: 0,
      rotate: -5 
    },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };
  
  // Icon animation variants
  const iconVariants = {
    hidden: { 
      x: -20, 
      opacity: 0 
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.2,
      rotate: 10,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  
  // Button animation variants
  const buttonVariants = {
    hidden: {
      scale: 0.9,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        delay: 0.8,
        duration: 0.4,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    tap: {
      scale: 0.98,
    },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className="relative w-full bg-cover bg-center py-16 px-4 sm:px-6 lg:px-12 overflow-hidden"
      style={{ backgroundImage: `url(${secBg})` }}
    >
      {/* Subtle background animation */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-blue-900/10 to-purple-900/10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      
      <motion.div 
        className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 text-white relative z-10"
        variants={containerVariants}
      >
        {/* Left Section */}
        <motion.div 
          className="w-full lg:w-1/2 text-center lg:text-left"
          variants={containerVariants}
        >
          <motion.h2
            style={{ lineHeight: "1.2" }}
            className="text-3xl md:text-4xl font-bold text-primaryBgColor mb-4"
            variants={itemVariants}
          >
            The Engage Network
          </motion.h2>
          
          <motion.p 
            className="text-secondaryTextColor text-base md:text-lg mb-6"
            variants={itemVariants}
          >
            We have partnered with leading corporations from diverse industries,
            along with Georgia Tech and Invest Georgia, to create a
            first-of-its-kind enterprise startup accelerator and corporate
            innovation platform.
          </motion.p>
          
          <motion.ul 
            className="space-y-4 mb-6 text-left"
            variants={containerVariants}
          >
            <motion.li 
              className="flex items-start gap-3"
              variants={itemVariants}
              whileHover={{ x: 5 }}
            >
              <motion.div
                variants={iconVariants}
                whileHover="hover"
              >
                <PiArrowBendDoubleUpRightFill className="text-primaryBtnHoverColor text-3xl md:text-4xl flex-shrink-0" />
              </motion.div>
              <span className="text-primaryBgColor text-base md:text-lg mt-1">
                Startups gain access to industry leaders, expert guidance, key
                market insights, and transformative opportunities for growth.
              </span>
            </motion.li>
            
            <motion.li 
              className="flex items-start gap-3"
              variants={itemVariants}
              whileHover={{ x: 5 }}
            >
              <motion.div
                variants={iconVariants}
                whileHover="hover"
              >
                <PiArrowBendDoubleUpRightFill className="text-primaryBtnHoverColor text-3xl md:text-4xl flex-shrink-0" />
              </motion.div>
              <span className="text-primaryBgColor text-base md:text-lg mt-1">
                Corporate partners benefit through cross-industry collaboration,
                early exposure to technologies, and innovative growth
                opportunities.
              </span>
            </motion.li>
          </motion.ul>
          
          <motion.button
            onClick={() => navigate("/procure-funds")}
            className="w-full md:w-1/2 lg:w-1/3 mt-4 px-6 py-3 bg-primaryBtnColor text-white rounded-full tracking-wider font-semibold flex items-center justify-center"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Start Consult <span className="text-2xl ml-2">◡̈</span>
          </motion.button>
        </motion.div>

        {/* Right Section */}
        <motion.div 
          className="w-full lg:w-1/2 flex justify-center"
          variants={imageVariants}
        >
          <motion.div
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.4 }
            }}
            className="relative"
          >
            <img
              src={secImg}
              alt="Investment"
              className="w-full max-w-md rounded-xl object-cover shadow-lg"
            />
            {/* Subtle glow effect on image hover */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-500/20 rounded-xl opacity-0"
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
      
      {/* Floating elements for visual interest */}
      <motion.div 
        className="absolute top-10 right-20 w-4 h-4 rounded-full bg-primaryBtnHoverColor"
        animate={{
          y: [0, -15, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div 
        className="absolute bottom-16 left-10 w-6 h-6 rounded-full bg-primaryBtnColor opacity-70"
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      />
    </motion.section>
  );
};

export default Network;